import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PoliticasContentDataSource } from './politicas-content-data-source';
import { ContentPage } from '../../models/content/content-page.model';
import { environment } from 'src/environments/environment';

/**
 * JSON file-based politicas content data source.
 * Reads from /content/politicas/*.json files for most pages.
 * Exception: 'terminos-condiciones' fetches the HTML document from S3.
 * Web uses the nginx/dev proxy path (/s3-regulations/); native apps resolve S3 directly in code.
 */
@Injectable()
export class JsonPoliticasContentDataSource extends PoliticasContentDataSource {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/content/politicas';

  private static readonly TERMINOS_S3_URL =
    'https://labanca-production.s3.amazonaws.com/regulations/_terminos_condiciones_info.html';

  private get terminosCondicionesUrl(): string {
    const configured = environment.regulations.terminosCondicionesUrl;
    // If the app was synced without --configuration=apps, the proxy path is invalid in WKWebView.
    if (Capacitor.isNativePlatform() && configured.startsWith('/')) {
      return JsonPoliticasContentDataSource.TERMINOS_S3_URL;
    }
    return configured;
  }

  getPage(slug: string): Observable<ContentPage> {
    if (slug === 'terminos-condiciones') {
      return this.fetchExternalHtmlPage(this.terminosCondicionesUrl, 'Términos y Condiciones').pipe(
        catchError(() => this.http.get<ContentPage>(`${this.basePath}/terminos-condiciones.json`))
      );
    }
    return this.http.get<ContentPage>(`${this.basePath}/${slug}.json`);
  }

  private fetchExternalHtmlPage(url: string, title: string): Observable<ContentPage> {
    return this.http.get(url, { responseType: 'text' }).pipe(
      map((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const bodyHtml = doc.body?.innerHTML ?? html;
        return {
          id: 'terminos-condiciones',
          slug: 'terminos-condiciones',
          title,
          breadcrumb: title,
          sections: [{ type: 'innerHtml' as const, content: '', innerHtml: bodyHtml }],
        };
      })
    );
  }
}
